import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { ArrowLeft, Save, Loader2, Wand2 } from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";

const blank = {
  code: "",
  customer_type: "individual",
  name: "",
  id_or_reg_number: "",
  vat_number: "",
  mobile: "",
  email: "",
  telephone: "",
  billing_address: "",
  account_terms: "cod",
  credit_limit: 0,
  notes: "",
  active: true,
};

const inputCls =
  "bg-white border-[#cfd7db] text-[#111619] focus:border-[#f28a16] focus:ring-1 focus:ring-[#f28a16] rounded-md h-10";

const Field = ({ label, hint, children, span = 1 }) => (
  <div className={`flex flex-col gap-1.5 ${span === 2 ? "sm:col-span-2" : ""}`}>
    <Label className="font-display uppercase tracking-wider text-xs font-bold text-[#3f4b52]">{label}</Label>
    {children}
    {hint && <div className="text-xs text-[#6b7480]">{hint}</div>}
  </div>
);

const Section = ({ title, children }) => (
  <section className="bg-white border border-[#cfd7db] rounded-md p-6 shadow-sm">
    <h2 className="font-display font-bold uppercase tracking-wider text-lg text-[#111619] border-l-4 border-[#f28a16] pl-3 mb-5">{title}</h2>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-4">{children}</div>
  </section>
);

export const AdminCustomerForm = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const nav = useNavigate();
  const [form, setForm] = useState(blank);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const codeTouched = useRef(false);

  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const c = await adminApi.getCustomer(id);
        setForm({ ...blank, ...c });
        codeTouched.current = true;
      } catch { toast.error("Could not load customer"); }
      finally { setLoading(false); }
    })();
  }, [id, isEdit]);

  // Auto-suggest code from name on Create (unless admin has typed one)
  useEffect(() => {
    if (isEdit) return;
    if (codeTouched.current) return;
    if (!form.name.trim()) { setForm((f) => ({ ...f, code: "" })); return; }
    const t = setTimeout(async () => {
      try {
        const res = await adminApi.suggestCustomerCode(form.name.trim());
        if (!codeTouched.current) setForm((f) => ({ ...f, code: res.code }));
      } catch { /* silent */ }
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [form.name]);

  const set = (k) => (e) => {
    const v = e && e.target ? (e.target.type === "checkbox" ? e.target.checked : e.target.value) : e;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const setCode = (e) => {
    codeTouched.current = true;
    setForm((f) => ({ ...f, code: e.target.value.toUpperCase() }));
  };

  const regenerateCode = async () => {
    if (!form.name.trim()) return toast.error("Enter a name first");
    try {
      const res = await adminApi.suggestCustomerCode(form.name.trim());
      codeTouched.current = false;
      setForm((f) => ({ ...f, code: res.code }));
      toast.success(`Suggested ${res.code}`);
    } catch { toast.error("Could not suggest a code"); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Customer name is required");
    setSaving(true);
    try {
      const payload = {
        ...form,
        credit_limit: Number(form.credit_limit) || 0,
        code: form.code.trim().toUpperCase() || null,
      };
      let saved;
      if (isEdit) saved = await adminApi.updateCustomer(id, payload);
      else saved = await adminApi.createCustomer(payload);
      toast.success(isEdit ? "Customer updated" : `Customer ${saved.code} created`);
      nav(`/admin/customers/${saved.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save customer");
    } finally { setSaving(false); }
  };

  if (loading) return <div className="p-10 text-center text-[#3f4b52]">Loading…</div>;

  return (
    <form onSubmit={submit} data-testid="admin-customer-form">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <Link to="/admin/customers" className="inline-flex items-center gap-1 text-xs font-display uppercase tracking-widest text-[#3f4b52] hover:text-[#f28a16]">
            <ArrowLeft size={14} /> Back to customers
          </Link>
          <h1 className="mt-1 font-display font-black uppercase text-3xl sm:text-4xl text-[#111619]">
            {isEdit ? "Edit customer" : "New customer"}
          </h1>
        </div>
        <button
          type="submit"
          disabled={saving}
          data-testid="customer-save-btn"
          className="bg-[#f28a16] text-white px-6 h-11 rounded-md font-display uppercase tracking-widest text-sm inline-flex items-center gap-2 hover:brightness-110 shadow disabled:opacity-60"
        >
          {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
          {saving ? "Saving" : (isEdit ? "Save changes" : "Create customer")}
        </button>
      </div>

      <div className="mt-4 max-w-5xl bg-[#fff8ea] border border-[#f28a16]/50 rounded-md p-3 text-xs text-[#7a5510]">
        <b>Customer</b> = the billed entity (insurer, fleet, glass shop, cash walk-in). It has a short code and receives invoices.
        <br />Brokers and end-user drivers now live in their own registers — the app will ask for those on the job itself.
      </div>

      <div className="mt-6 space-y-6 max-w-5xl">
        <Section title="Identification">
          <Field label="Customer type">
            <select value={form.customer_type} onChange={set("customer_type")} className={`${inputCls} px-3`} data-testid="cust-type">
              <option value="individual">Individual</option>
              <option value="company">Company / Fleet / Insurer</option>
              <option value="cod">Once-off / COD (cash walk-in)</option>
            </select>
          </Field>
          <Field label="Customer code" hint="Auto-generated from name (e.g. AUT001). You can override before saving.">
            <div className="flex gap-2">
              <Input value={form.code} onChange={setCode} className={`${inputCls} font-mono`} data-testid="cust-code" placeholder="AUT001" />
              {!isEdit && (
                <button
                  type="button"
                  onClick={regenerateCode}
                  className="bg-[#20282c] text-white h-10 px-3 rounded-md text-xs font-display uppercase tracking-widest inline-flex items-center gap-1"
                  title="Regenerate suggested code from name"
                >
                  <Wand2 size={12} /> Auto
                </button>
              )}
            </div>
          </Field>
          <Field label="Name / company *" span={2}>
            <Input value={form.name} onChange={set("name")} className={inputCls} data-testid="cust-name" />
          </Field>
          <Field label="Company reg. number">
            <Input value={form.id_or_reg_number} onChange={set("id_or_reg_number")} className={inputCls} data-testid="cust-reg-number" />
          </Field>
          <Field label="VAT number">
            <Input value={form.vat_number} onChange={set("vat_number")} className={inputCls} data-testid="cust-vat" />
          </Field>
        </Section>

        <Section title="Contact">
          <Field label="Mobile"><Input value={form.mobile} onChange={set("mobile")} className={inputCls} /></Field>
          <Field label="Email"><Input type="email" value={form.email} onChange={set("email")} className={inputCls} /></Field>
          <Field label="Telephone"><Input value={form.telephone} onChange={set("telephone")} className={inputCls} /></Field>
          <Field label="Billing address" span={2}>
            <Textarea value={form.billing_address} onChange={set("billing_address")} rows={2} className="bg-white border-[#cfd7db]" />
          </Field>
        </Section>

        <Section title="Account">
          <Field label="Terms">
            <select value={form.account_terms} onChange={set("account_terms")} className={`${inputCls} px-3`}>
              <option value="cod">C.O.D. (cash on delivery)</option>
              <option value="30_days">Nett 30 days</option>
              <option value="60_days">Nett 60 days</option>
            </select>
          </Field>
          <Field label="Credit limit (ZAR)">
            <Input type="number" step="0.01" value={form.credit_limit} onChange={set("credit_limit")} className={inputCls} />
          </Field>
          <Field label="Internal notes" span={2}>
            <Textarea value={form.notes} onChange={set("notes")} rows={3} className="bg-white border-[#cfd7db]" />
          </Field>
          <Field label="Active">
            <label className="inline-flex items-center gap-2 text-sm text-[#3f4b52] h-10">
              <input type="checkbox" checked={form.active} onChange={set("active")} className="h-4 w-4 accent-[#f28a16]" data-testid="cust-active" />
              {form.active ? "Active — appears in pickers" : "Inactive — hidden from pickers"}
            </label>
          </Field>
        </Section>
      </div>
    </form>
  );
};

export default AdminCustomerForm;
